// Die Startseite des Verkäufers (Mockup /doku/start): oben die Begrüßung
// und ob alles beim Büro ist, darunter „Neue Besichtigung“, „Läuft gerade“
// und „Gesendet“. Was später kommt, steht schon als Platzhalter da
// (CLAUDE.md, Grundsatz 8).
//
// Seit E2, Schritt 4: „Gesendet“ zeigt die Besichtigungen, die abgeschlossen
// oder im Büro sind – antippen öffnet sie zum Nachsehen. Beim Öffnen und wenn
// das Netz zurückkommt, gleicht die Seite mit dem Server ab (einsicht.ts).
// Abmelden geht erst, wenn nichts mehr wartet: Der Postausgang ist an diese
// Anmeldung gebunden.
//
// Seit Schritt 5: Eine Besichtigung, an der gerade etwas nachgereicht wird,
// steht auch unter „Läuft gerade“ (mit dem Chip „Nachtrag“) – damit der
// Nachtrag nicht vergessen wird. Unter „Gesendet“ sagt ihr Chip „Nachtrag
// läuft“ oder, während er gesendet wird, „Nachtrag wartet“.
//
// Seit E3 (S6, Gernots Entscheidung 2 – kein Web-Push, dafür nicht zu
// übersehen): Ganz oben das Band „2 offene Fragen aus dem Büro – antworten“
// in Lime; unter „Gesendet“ je Besichtigung die Marke „1 Frage“. Gezählt wird,
// was auf den Verkäufer wartet (gespraech.ts, `wartetAufMich`). Damit eine
// neue Frage auch ankommt, wenn die App nur wieder nach vorne geholt wird
// (am iPhone lädt sie dann nicht neu), gleicht die Seite auch dann ab – und
// jede Minute, solange sie offen ist.
//
// Seit E3b: Bei denselben Gelegenheiten holt sie die Liste zum Antippen für
// „Material“ vom Büro (material.ts) – damit sie draußen schon am Gerät ist.

import { useEffect } from "react";
import type { IchAntwort } from "@happy-energy/kern";
import { AppKopf, Chip, FASSUNG, Ikon, Spaeter, Tipp } from "@happy-energy/oberflaeche";
import { speicher, type Aufnahme, type Nachtrag } from "../speicher.ts";
import { STATIONEN_ZAHL, stationenFertig } from "../postausgang.ts";
import { EINSICHT_TAKT, einsichtAnstossen, wannZeigen } from "../einsicht.ts";
import { nachtragUnterwegs, offenerNachtrag } from "../arbeitskopie.ts";
import { fragenUeberblickLesen } from "../gespraech.ts";
import { standardlisteAnstossen } from "../material.ts";
import { anzahl, inSafariStattApp, kuerzel, SendeStand, useLive, usePostausgang } from "./gemeinsam.tsx";
import { FrageMarke, FragenBand } from "./fragen.tsx";

function begruessung(): string {
  const stunde = new Date().getHours();
  if (stunde < 11) return "Guten Morgen";
  if (stunde < 18) return "Hallo";
  return "Guten Abend";
}

interface LaufendeZeile {
  id: string;
  name: string;
  ort: string;
  fertig: number;
  fehler: string | null;
  /** Läuft hier ein Nachtrag (seit Schritt 5): seit wann – sonst null. */
  nachtragSeit: string | null;
  /** Wonach die Liste ordnet: neueste zuerst. */
  seit: string;
}

/** Was gerade läuft: Besichtigungen, die aufgenommen werden, und solche, an denen etwas nachgereicht wird. */
async function laufendeLesen(): Promise<LaufendeZeile[]> {
  const aufnahmen = await speicher.aufnahmen.where("stand").equals("in_aufnahme").toArray();
  const offen = (await speicher.nachtraege.toArray()).filter((n) => n.stand === "offen");
  const imNachtrag = (await speicher.aufnahmen.bulkGet(offen.map((n) => n.aufnahmeId))).flatMap((a, i) =>
    a?.stand === "im_buero" ? [{ a, n: offen[i] }] : [],
  );
  const zeilen = await Promise.all(
    [...aufnahmen.map((a) => ({ a, n: undefined as Nachtrag | undefined })), ...imNachtrag].map(async ({ a, n }) => ({
      id: a.id,
      name: a.kunde.name,
      ort: `${a.objekt.strasse}, ${a.objekt.ort}`,
      fertig: stationenFertig(await speicher.stationen.where("aufnahmeId").equals(a.id).toArray()),
      fehler: a.fehler,
      nachtragSeit: n?.begonnenAm ?? null,
      seit: n?.begonnenAm ?? a.begonnenAm,
    })),
  );
  return zeilen.sort((x, y) => y.seit.localeCompare(x.seit));
}

/** Wann eine gesendete Besichtigung zuletzt etwas tat: im Büro bestätigt, sonst abgeschlossen. */
function gesendetZeit(a: Aufnahme): string {
  return a.imBueroAm ?? a.abgeschlossenAm ?? a.begonnenAm;
}

interface GesendeteZeile {
  aufnahme: Aufnahme;
  nachtraege: Nachtrag[];
}

/** Abgeschlossen oder im Büro – neueste zuerst, mit ihren Nachträgen. */
async function gesendeteLesen(): Promise<GesendeteZeile[]> {
  const aufnahmen = await speicher.aufnahmen.where("stand").anyOf(["abgeschlossen", "im_buero"]).toArray();
  const nachtraege = await speicher.nachtraege.where("aufnahmeId").anyOf(aufnahmen.map((a) => a.id)).toArray();
  return aufnahmen
    .sort((a, b) => gesendetZeit(b).localeCompare(gesendetZeit(a)))
    .map((aufnahme) => ({ aufnahme, nachtraege: nachtraege.filter((n) => n.aufnahmeId === aufnahme.id) }));
}

function GesendetStand({ aufnahme, nachtraege }: GesendeteZeile) {
  if (aufnahme.fehler) {
    return (
      <Chip art="fehler" ikon="achtung">
        Fehler
      </Chip>
    );
  }
  // Ein Nachtrag läuft oder wird gesendet (seit Schritt 5) – das soll man hier sehen, nicht nur unter „Läuft gerade“.
  if (offenerNachtrag(nachtraege)) {
    return (
      <Chip art="lime" ikon="plus">
        Nachtrag läuft
      </Chip>
    );
  }
  if (nachtragUnterwegs(nachtraege)) {
    return (
      <Chip art="warn" ikon="uhr">
        Nachtrag wartet
      </Chip>
    );
  }
  if (aufnahme.stand === "im_buero") {
    return (
      <Chip art="ok" ikon="haken">
        im Büro
      </Chip>
    );
  }
  return (
    <Chip art="warn" ikon="uhr">
      wartet
    </Chip>
  );
}

export function Startseite({ ich, abmelden }: { ich: IchAntwort; abmelden: () => void }) {
  const vorname = ich.nutzer.name.split(" ")[0];
  const laufende = useLive(laufendeLesen, "laufende");
  const gesendete = useLive(gesendeteLesen, "gesendete");
  const ichId = ich.nutzer.id;
  const firmaId = ich.firma.id;
  const fragen = useLive(() => fragenUeberblickLesen(ichId), `fragen-${ichId}`);
  // Seit E3c (S6): was das Büro gelöscht hat – einmal sagen, bis „Verstanden“ (wegraeumen.ts). Seit der
  // Code-Prüfung E3c ebenso: verworfen, aber das Büro arbeitet schon mit der Anlage (sender.ts).
  const hinweise = useLive(() => speicher.hinweise.toArray(), "hinweise");
  const post = usePostausgang();

  // Mit dem Server abgleichen: beim Öffnen, wenn das Netz zurückkommt, wenn die App wieder nach vorne kommt
  // (seit E3 – dann lädt sie am iPhone nicht neu) und jede Minute (höchstens etwa einmal je Minute).
  useEffect(() => {
    const anstossen = () => {
      einsichtAnstossen();
      standardlisteAnstossen(firmaId);
    };
    const vorne = () => {
      if (document.visibilityState === "visible") anstossen();
    };
    anstossen();
    window.addEventListener("online", anstossen);
    document.addEventListener("visibilitychange", vorne);
    const uhr = window.setInterval(vorne, EINSICHT_TAKT);
    return () => {
      window.removeEventListener("online", anstossen);
      document.removeEventListener("visibilitychange", vorne);
      window.clearInterval(uhr);
    };
  }, [firmaId]);

  return (
    <div className="app-seite">
      <AppKopf
        umgebung={ich.umgebung}
        rechts={<SendeStand wartend={post.wartend} />}
        gross={`${begruessung()}, ${vorname}!`}
        unter={`${new Date().toLocaleDateString("de-AT", { weekday: "long", day: "numeric", month: "long" })} · ${ich.firma.name}`}
      />
      <main className="app-inhalt">
        {/* Ganz oben, vor allem anderen: was auf den Verkäufer wartet (Gernots Entscheidung 2). */}
        {fragen?.erste && (
          <FragenBand
            zahl={fragen.zahl}
            erste={fragen.erste}
            unter={`${fragen.zahl > 1 ? "Zuerst: " : ""}${fragen.erste.kundeName}, ${fragen.erste.ort.wo}`}
          />
        )}

        {inSafariStattApp() && (
          <Tipp art="warn" ikon="achtung">
            <b>Bitte die App vom Home-Bildschirm öffnen</b> – nur dort bleiben deine Besichtigungen gespeichert. In Safari hat sie einen
            eigenen Speicher.
          </Tipp>
        )}

        {hinweise?.map((h) => (
          <Tipp key={h.id} art="grau" ikon="info">
            {h.art === "buero-arbeitet" ? (
              // Seit der Code-Prüfung E3c (Befund 3): verworfen, aber das Büro arbeitet schon damit (sender.ts).
              <>
                <b>Das Büro arbeitet schon mit dieser Anlage:</b> {h.ort ? `${h.name}, ${h.ort}` : h.name} – von diesem Gerät ist sie weg,
                löschen kann sie nur die Leitung im CRM.
              </>
            ) : (
              <>
                <b>Das Büro hat diese Besichtigung gelöscht:</b> {h.name}, {h.ort} – sie ist auch von diesem Gerät weg.
              </>
            )}
            <button type="button" className="tipp-knopf" onClick={() => void speicher.hinweise.delete(h.id)}>
              Verstanden
            </button>
          </Tipp>
        ))}

        <a href="#/neu" className="neu-karte">
          <span className="avatar avatar-dunkel">
            <Ikon name="plus" groesse="g" />
          </span>
          <span className="neu-wort">
            <b>Neue Besichtigung</b>
            <span>Kunde anlegen und loslegen</span>
          </span>
          <Ikon name="rechts" />
        </a>

        <div className="abschnitt-titel">Läuft gerade</div>
        {laufende === undefined ? null : laufende.length === 0 ? (
          <div className="leer-karte">Gerade läuft keine Besichtigung. Tippe oben auf „Neue Besichtigung“.</div>
        ) : (
          <div className="listen-karte">
            {laufende.map((a) => {
              const wartet = post.jeAufnahme[a.id] ?? 0;
              return (
                <a key={a.id} href={`#/aufnahme/${a.id}`} className="listen-eintrag">
                  <span className={`avatar ${a.nachtragSeit ? "avatar-lime" : "avatar-tuerkis"}`}>{kuerzel(a.name)}</span>
                  <span className="listen-wort">
                    <b>{a.name}</b>
                    <span>{a.ort}</span>
                    <span>
                      {a.nachtragSeit ? `Etwas nachreichen · seit ${wannZeigen(a.nachtragSeit)}` : `${a.fertig} von ${STATIONEN_ZAHL} Stationen`}
                    </span>
                  </span>
                  {a.fehler ? (
                    <Chip art="fehler" ikon="achtung">
                      Fehler
                    </Chip>
                  ) : a.nachtragSeit ? (
                    <Chip art="lime" ikon="plus">
                      Nachtrag
                    </Chip>
                  ) : wartet > 0 ? (
                    <Chip art="warn" ikon="uhr">
                      wartet
                    </Chip>
                  ) : (
                    <Ikon name="rechts" />
                  )}
                </a>
              );
            })}
          </div>
        )}

        <div className="abschnitt-titel">Gesendet</div>
        {gesendete === undefined ? null : gesendete.length === 0 ? (
          <div className="leer-karte">Noch nichts gesendet. Was beim Büro ankommt, steht hier – zum Nachsehen.</div>
        ) : (
          <div className="listen-karte">
            {gesendete.map(({ aufnahme: a, nachtraege }) => {
              const offen = fragen?.jeAufnahme[a.id] ?? 0;
              return (
                <a key={a.id} href={`#/aufnahme/${a.id}`} className="listen-eintrag">
                  <span className="avatar avatar-lime">{kuerzel(a.kunde.name)}</span>
                  <span className="listen-wort">
                    <b>{a.kunde.name}</b>
                    <span>
                      {a.objekt.strasse} · {wannZeigen(gesendetZeit(a))}
                    </span>
                  </span>
                  <span className="listen-chips">
                    <GesendetStand aufnahme={a} nachtraege={nachtraege} />
                    {offen > 0 && <FrageMarke>{anzahl(offen, "1 Frage", "# Fragen")}</FrageMarke>}
                  </span>
                </a>
              );
            })}
          </div>
        )}

        <div className="abschnitt-titel">Später</div>
        <Spaeter titel="Neue Leads für dich" wann="Stufe 3 · Kiste Lead">
          Am Morgen neue Anfragen aus dem Lead-Management, mit Telefonnummer – gleich anrufen.
        </Spaeter>
        <Spaeter titel="Deine Termine heute" wann="Kiste Termin">
          Besichtigungen und Montagetermine aus dem Terminmanagement.
        </Spaeter>

        <div className="fuss-zeilen">
          <span>
            Angemeldet als {ich.nutzer.name} auf „{ich.geraet.name}“ ·{" "}
            {/* Was noch wartet, geht nur mit dieser Anmeldung hinaus – abmelden erst danach. */}
            {post.wartend > 0 ? (
              <span className="abmelden-wartet">abmelden geht, sobald alles gesendet ist</span>
            ) : (
              <a href="#/" onClick={abmelden}>
                abmelden
              </a>
            )}
          </span>
          <span className="fassung">Fassung {FASSUNG}</span>
        </div>
      </main>
    </div>
  );
}
